import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config/api';

interface Tailor {
  id: number;
  name: string;
  email: string;
  role: string;
  avatar_url?: string;
  is_available?: boolean;
}

interface TailorSelectionProps {
  onTailorSelect: (tailor: Tailor) => void;
  onCancel: () => void;
}

export function TailorSelection({ onTailorSelect, onCancel }: TailorSelectionProps) {
  const [tailors, setTailors] = useState<Tailor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTailor, setSelectedTailor] = useState<Tailor | null>(null);

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    fetchAvailableTailors();
  }, []);

  const fetchAvailableTailors = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE}/api/appointments/available-tailors`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tailors: ${response.status}`);
      }

      const data = await response.json();
      console.log('Tailors API response:', data);

      // Handle different response structures
      let tailorsArray: Tailor[] = [];
      if (Array.isArray(data)) {
        tailorsArray = data;
      } else if (data.tailors && Array.isArray(data.tailors)) {
        tailorsArray = data.tailors;
      } else if (data.data && Array.isArray(data.data)) {
        tailorsArray = data.data;
      }

      // Filter for tailors only
      const tailorUsers = tailorsArray.filter((tailor: any) => 
        tailor.role === 'TAILOR' || tailor.role === 'tailor'
      );

      console.log('Filtered tailors:', tailorUsers);
      setTailors(tailorUsers);
    } catch (_err) {
      console.error('Failed to fetch tailors:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch tailors');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTailorSelect = (tailor: Tailor) => {
    setSelectedTailor(tailor);
  };

  const handleConfirmSelection = () => {
    if (selectedTailor) {
      onTailorSelect(selectedTailor);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading available tailors...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-lg font-medium">Unable to load tailors</p>
            <p className="text-sm text-gray-600 mt-2">{error}</p>
          </div>
          <button 
            onClick={fetchAvailableTailors}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (tailors.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-lg font-medium">No tailors available</p>
            <p className="text-sm text-gray-600 mt-2">Please contact support to get assigned a tailor</p>
          </div>
          <button 
            onClick={onCancel}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Select a Tailor</h2>
            <p className="text-gray-600 mt-1">Choose a tailor to start messaging with</p>
          </div>
        </div>

        <div className="space-y-4">
          {tailors.map((tailor) => (
            <div
              key={tailor.id}
              className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                selectedTailor?.id === tailor.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleTailorSelect(tailor)}
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-lg">
                    {tailor.name?.charAt(0) || 'T'}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{tailor.name || 'Tailor'}</h3>
                  <p className="text-sm text-gray-500">{tailor.email}</p>
                  <div className="flex items-center mt-1">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Available
                    </span>
                  </div>
                </div>
                <div className="flex items-center">
                  {selectedTailor?.id === tailor.id && (
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmSelection}
            disabled={!selectedTailor}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Start Chatting
          </button>
        </div>
      </div>
    </div>
  );
}
